import { Switch } from "@/components/ui/switch";
import { useInventoryConfig, useSetInventoryEnabled } from "@/hooks/useInventory";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/**
 * The tenant's own stock switch.
 *
 * Deliberately NOT a plan feature: the plan says what we sold (`orders`), this
 * says how the shop operates — the `whatsapp_broadcasts_enabled` split, where an
 * upgrade fixes one and only the shop fixes the other. A bordered card with a
 * Switch rather than a checkbox for the same reason that one uses it: this is a
 * persisted policy that changes what the agent tells real customers, not a view
 * filter.
 *
 * The copy states the rule that surprises people: turning it ON does not stop
 * anything from selling. A product with no count is untracked, not sold out.
 */
export function InventoryToggleCard({
  canEdit,
}: {
  canEdit: boolean;
}): React.JSX.Element | null {
  const { data: config } = useInventoryConfig();
  const setEnabled = useSetInventoryEnabled();

  if (!config) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ink-100 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[13px] font-medium text-ink-800">Control de existencias</p>
          <p className="text-[12.5px] text-ink-500">
            Con esto encendido, el agente consulta las existencias reales y no cierra un
            pedido que no se puede surtir. Los productos sin cantidad capturada se siguen
            vendiendo igual que antes.
          </p>
        </div>
        <Switch
          checked={config.enabled}
          disabled={!canEdit || setEnabled.isPending}
          aria-label="Control de existencias"
          onCheckedChange={(checked) => setEnabled.mutate(checked)}
        />
      </div>
      {config.enabledAt && (
        <p className="text-[11.5px] text-ink-500">
          Último cambio: {formatDate(config.enabledAt)}
        </p>
      )}
    </div>
  );
}
